"use server";

interface IConsultation {
  name: string;
  email: string;
  phone: string;
  company: string;
  topic: string;
  date: string;
  duration: string;
  timezone: string;
  notes: string;
  meetingType: string;
  status: string;
  meetingLink?: string;
  phoneNumber?: string;
  participants?: string[];
}

import prisma from "@/lib/db";
import { sendMeetingConfirmationEmail } from "@/lib/email";
import bcrypt from "bcryptjs";

interface EmailCheckResult {
  status: boolean; // true if email exists
  isVerified?: boolean; // user verification status if exists
  message: string;
}

export const changeOldPassword = async (
  userId: number,
  currentPassword: string,
  newPassword: string
): Promise<{ success: boolean; message: string }> => {
  try {
    const user = await prisma.user.findUnique({ where: { id: userId } });
    if (!user) {
      return { success: false, message: "User not found." };
    }

    // Check for 3 days password change restriction
    if (user.resetedOn) {
      const THREE_DAYS = 3 * 24 * 60 * 60 * 1000;
      const timeSinceLastChange =
        Date.now() - new Date(user.resetedOn).getTime();
      if (timeSinceLastChange < THREE_DAYS) {
        return {
          success: false,
          message: "You can only change your password once every 3 days.",
        };
      }
    }

    const isPasswordValid = bcrypt.compareSync(currentPassword, user.password);
    if (!isPasswordValid) {
      return { success: false, message: "Current password is incorrect." };
    }

    // Encrypt password
    const salt = bcrypt.genSaltSync(10);
    const hashedPassword = bcrypt.hashSync(newPassword, salt);

    await prisma.user.update({
      where: { id: userId },
      data: { password: hashedPassword, resetedOn: new Date() },
    });
    return { success: true, message: "Password changed successfully." };
  } catch (error) {
    return {
      success: false,
      message: "An error occurred while changing the password.",
    };
  }
};

export const checkEmailExists = async (
  email: string
): Promise<EmailCheckResult> => {
  try {
    const existingUser = await prisma.user.findUnique({ where: { email } });

    if (existingUser) {
      return {
        status: true,
        isVerified: existingUser.verified,
        message: "Email exists",
      };
    }

    return {
      status: false,
      message: "Email does not exist",
    };
  } catch (error) {
    console.error(error);
    return {
      status: false,
      message: "An error occurred while checking the email",
    };
  }
};

export const verifyEmail = async (
  email: string,
  code: string
): Promise<{ success: boolean; message: string }> => {
  try {
    // 1. Find user
    const user = await prisma.user.findUnique({ where: { email } });
    if (!user) {
      return { success: false, message: "User not found." };
    }

    if (user.verified) {
      return { success: false, message: "Email is already verified." };
    }

    // 2. Check code
    if (user.resendCode !== code) {
      return { success: false, message: "Invalid verification code." };
    }

    if (user.resentOn) {
      const SIXTY_MINUTES = 60 * 60 * 1000;

      const timeSinceLastResend =
        Date.now() - new Date(user.resentOn).getTime();
      if (timeSinceLastResend > SIXTY_MINUTES) {
        return {
          success: false,
          message: "Verification code has expired. Please request a new code.",
        };
      }
    }

    // 3. Verify user
    await prisma.user.update({
      where: { email },
      data: { verified: true, resendCode: null, verifiedOn: new Date() },
    });

    return { success: true, message: "Email verified successfully." };
  } catch (error) {
    console.error(error);
    return {
      success: false,
      message: "An error occurred during verification.",
    };
  }
};

export const createConsultation = async (
  data: IConsultation
): Promise<{ success: boolean; message: string }> => {
  try {
    const existingConsultation = await prisma.consultation.findFirst({
      where: {
        email: data.email,
        date: new Date(data.date),
      },
    });

    if (existingConsultation) {
      return {
        success: false,
        message: "You already have a consultation scheduled at this time.",
      };
    }

    const user = await prisma.user.findUnique({
      where: { email: data.email },
    });

    if (!user) {
      return {
        success: false,
        message: "No user found with the provided email.",
      };
    }

    await prisma.consultation.create({
      data: {
        // Step 1: Client info
        name: data.name,
        email: data.email,
        phone: data.phone,
        company: data.company,

        // Step 2: Calendar info
        date: new Date(data.date),
        duration: Number(data.duration), // MUST be Int
        timezone: data.timezone,

        // Step 3: Meeting details
        topic: data.topic,
        meetingType: data.meetingType ?? "video",
        status: "pending",
        meetingLink: data.meetingLink || null,
        phoneNumber: data.phoneNumber || null,
        notes: data.notes || null,

        // User relation
        user: {
          connect: { id: user.id },
        },

        // Participants (nested create)
        participants:
          data.participants && data.participants.length > 0
            ? {
                create: data.participants
                  .filter((email: string) => !!email) // avoid undefined/null
                  .map((email: string) => ({
                    email,
                  })),
              }
            : undefined,
      },
    });

    // Send email
    await sendMeetingConfirmationEmail({
      ...data,
      to: data.email,
      participants: data.participants || [],
    } as any);

    return { success: true, message: "Consultation scheduled successfully." };
  } catch (error) {
    console.log("Error creating consultation:", error);
    return {
      success: false,
      message: "An error occurred while scheduling the consultation.",
    };
  }
};

export const updateConsultation = async (
  id: string,
  data: Partial<IConsultation>
): Promise<{ success: boolean; message: string }> => {
  // console.log("Updating consultation:", id, data);

  try {
    // Make sure the consultation exists
    const consultation = await prisma.consultation.findUnique({
      where: { id },
      include: { participants: true },
    });

    if (!consultation) {
      return { success: false, message: "Consultation not found." };
    }

    // Optional: prevent double-booking when date changes
    if (data.date && data.email) {
      const existing = await prisma.consultation.findFirst({
        where: {
          email: data.email,
          date: new Date(data.date),
          NOT: { id }, // exclude current one
        },
      });

      if (existing) {
        return {
          success: false,
          message: "You already have a consultation scheduled at this time.",
        };
      }
    }

    const user = await prisma.user.findUnique({
      where: { email: data.email },
    });

    if (!user || user.id !== consultation.userId) {
      return {
        success: false,
        message: "No user found with the provided email.",
      };
    }

    // If participants exist, we replace them
    let participantUpdate = undefined;

    if (data.participants) {
      participantUpdate = {
        deleteMany: {}, // remove old
        create: data.participants
          .filter((email: string) => !!email)
          .map((email: string) => ({ email })),
      };
    }

    await prisma.consultation.update({
      where: { id },
      data: {
        name: data.name ?? consultation.name,
        email: data.email ?? consultation.email,
        phone: data.phone ?? consultation.phone,
        company: data.company ?? consultation.company,

        date: data.date ? new Date(data.date) : consultation.date,
        duration: data.duration ? Number(data.duration) : consultation.duration,
        timezone: data.timezone ?? consultation.timezone,

        topic: data.topic ?? consultation.topic,
        meetingType: data.meetingType ?? consultation.meetingType,
        status: data.status ?? consultation.status,
        meetingLink: data.meetingLink ?? consultation.meetingLink,
        phoneNumber: data.phoneNumber ?? consultation.phoneNumber,
        notes: data.notes ?? consultation.notes,

        participants: participantUpdate,
      },
    });

    return { success: true, message: "Consultation updated successfully." };
  } catch (error) {
    console.log("Error updating consultation:", error);
    return {
      success: false,
      message: "An error occurred while updating the consultation.",
    };
  }
};

// Accept consultation only by admin
export const acceptConsultation = async ({
  id,
  isAdmin,
  message,
}: {
  id: string;
  isAdmin: boolean;
  message: string;
}): Promise<{ success: boolean; message: string }> => {
  try {
    const consultation = await prisma.consultation.findUnique({
      where: { id },
    });
    if (!consultation) {
      return { success: false, message: "Consultation not found." };
    }

    if (!isAdmin) {
      return { success: false, message: "Unauthorized action." };
    }

    // Check if time has passed
    const now = new Date();
    if (consultation.date < now) {
      return {
        success: false,
        message: "Cannot accept a consultation scheduled in the past.",
      };
    }

    await prisma.consultation.update({
      where: { id },
      data: { status: "confirmed", adminMessage: message || null },
    });

    return { success: true, message: "Consultation accepted successfully." };
  } catch (error) {
    console.log("Error accepting consultation:", error);
    return {
      success: false,
      message: "An error occurred while accepting the consultation.",
    };
  }
};

// Decline consultation only by admin
export const declineConsultation = async ({
  id,
  isAdmin,
  message,
}: {
  id: string;
  isAdmin: boolean;
  message: string;
}): Promise<{ success: boolean; message: string }> => {
  try {
    const consultation = await prisma.consultation.findUnique({
      where: { id },
    });
    if (!consultation) {
      return { success: false, message: "Consultation not found." };
    }

    if (!isAdmin) {
      return { success: false, message: "Unauthorized action." };
    }

    // Check if time has passed
    const now = new Date();
    if (consultation.date < now) {
      return {
        success: false,
        message: "Cannot decline a consultation scheduled in the past.",
      };
    }

    await prisma.consultation.update({
      where: { id },
      data: { status: "declined", adminMessage: message || null },
    });

    return { success: true, message: "Consultation declined successfully." };
  } catch (error) {
    console.log("Error declining consultation:", error);
    return {
      success: false,
      message: "An error occurred while declining the consultation.",
    };
  }
};

// Fetch all consultations (admin)
export const fetchAllConsultations = async (): Promise<{
  success: boolean;
  message: string;
  data?: any[];
}> => {
  try {
    const consultations = await prisma.consultation.findMany({
      orderBy: {
        date: "desc",
      },
      include: {
        user: true,
        participants: true,
      },
    });

    return {
      success: true,
      message: "Consultations fetched successfully.",
      data: consultations,
    };
  } catch (error) {
    console.log("Error fetching consultations:", error);
    return {
      success: false,
      message: "An error occurred while fetching consultations.",
    };
  }
};

// Fetch consultations by email (user)
export const fetchConsultationsByEmail = async (
  email: string
): Promise<{
  success: boolean;
  message: string;
  data?: any[];
}> => {
  try {
    const consultations = await prisma.consultation.findMany({
      where: {
        email,
      },
      orderBy: {
        date: "desc",
      },
      include: {
        participants: true,
      },
    });

    return {
      success: true,
      message: "Consultations fetched successfully.",
      data: consultations,
    };
  } catch (error) {
    console.log("Error fetching consultations:", error);
    return {
      success: false,
      message: "An error occurred while fetching consultations.",
    };
  }
};
